import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2, Plus, Lock, TrendingUp, TrendingDown, FileDown, FlaskConical, ArrowRight, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useFinance } from "@/context/FinanceContext";
import { txInRange } from "@/lib/finance";
import { computeDailyHealth } from "@/lib/dailyHealth";
import { weeklyRecap } from "@/lib/insights";
import { exportInsightPdf } from "@/lib/exportInsightPdf";
import { DailyHealthPanel } from "@/components/DailyHealth";
import { LogReminderBanner } from "@/components/LogReminder";
import { StreakCard } from "@/components/StreakCard";
import { MonthlySummary } from "@/components/MonthlySummary";
import { WeeklyRecapCard } from "@/components/WeeklyRecap";
import { MonthlyTrends } from "@/components/MonthlyTrends";
import { BudgetTargets } from "@/components/BudgetTargets";
import { formatCompact, formatDateID } from "@/lib/currency";

const RANGES = [
  { id: "day", label: "Hari" },
  { id: "week", label: "Minggu" },
  { id: "month", label: "Bulan" },
];

export default function Catatan() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { finance, deleteTransaction } = useFinance();
  const [tab, setTab] = useState(params.get("tab") === "analisis" ? "analisis" : "transaksi");
  const [range, setRange] = useState("month");

  useEffect(() => {
    if (params.get("tab") === "analisis") setTab("analisis");
  }, [params]);

  const all = finance?.transactions || [];
  const items = txInRange(all, range);
  const income = items.filter((t) => t.type === "income").reduce((s, t) => s + (t.amount || 0), 0);
  const expense = items.filter((t) => t.type === "expense").reduce((s, t) => s + (t.amount || 0), 0);

  // Daily health recomputes automatically whenever transactions change.
  const health = useMemo(() => computeDailyHealth(finance), [finance]);
  const recap = useMemo(() => weeklyRecap(finance), [finance]);

  return (
    <div className="px-6 pt-8" data-testid="catatan-page">
      <h1 className="mb-1 font-heading text-2xl font-extrabold text-zinc-900">Catatan</h1>
      <p className="mb-5 text-sm text-zinc-400">Aktivitas keuangan keluarga Anda</p>

      <LogReminderBanner className="mb-4" />
      <StreakCard className="mb-5" />

      {/* Tabs */}
      <div className="mb-5 flex gap-2 rounded-full bg-zinc-100 p-1 lg:max-w-md">
        {[
          { id: "transaksi", label: "Transaksi" },
          { id: "analisis", label: "Analisis" },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`catatan-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex-1 rounded-full py-2 text-sm font-semibold transition-colors ${
              tab === t.id ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "transaksi" ? (
        /* Desktop: 2-pane (kontrol kiri, daftar kanan); mobile: urutan menumpuk tak berubah */
        <div className="lg:grid lg:grid-cols-12 lg:gap-6 lg:items-start">
          <div className="lg:col-span-5">
            <div className="mb-4 flex gap-2 rounded-full bg-zinc-100 p-1" data-testid="range-toggle">
              {RANGES.map((r) => (
                <button
                  key={r.id}
                  data-testid={`range-${r.id}`}
                  onClick={() => setRange(r.id)}
                  className={`flex-1 rounded-full py-1.5 text-xs font-semibold transition-colors ${
                    range === r.id ? "bg-emerald-600 text-white" : "text-zinc-500"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            <div className="mb-5 grid grid-cols-2 gap-3 lg:mb-0">
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                <div className="flex items-center gap-1.5 text-emerald-700">
                  <TrendingUp className="h-3.5 w-3.5" />
                  <span className="text-[11px] font-semibold">Pemasukan</span>
                </div>
                <p data-testid="sum-income" className="mt-1 font-mono text-lg font-bold text-emerald-700">
                  {formatCompact(income)}
                </p>
              </div>
              <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4">
                <div className="flex items-center gap-1.5 text-rose-600">
                  <TrendingDown className="h-3.5 w-3.5" />
                  <span className="text-[11px] font-semibold">Pengeluaran</span>
                </div>
                <p data-testid="sum-expense" className="mt-1 font-mono text-lg font-bold text-rose-600">
                  {formatCompact(expense)}
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {items.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-zinc-200 p-8 text-center" data-testid="tx-empty">
                <p className="text-sm text-zinc-400">Belum ada transaksi pada periode ini.</p>
                <Button
                  data-testid="tx-empty-add"
                  onClick={() => navigate("/app/catat")}
                  className="mt-4 h-10 rounded-full bg-zinc-900 text-sm font-semibold"
                >
                  <Plus className="mr-1.5 h-4 w-4" /> Catat sekarang
                </Button>
              </div>
            ) : (
              <div className="space-y-2 lg:max-h-[70vh] lg:overflow-y-auto lg:pr-1" data-testid="tx-list">
                {items.map((t) => (
                  <div
                    key={t.id}
                    data-testid={`tx-item-${t.id}`}
                    className="flex items-center justify-between rounded-2xl border border-zinc-100 bg-white p-4 shield-shadow"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-heading text-sm font-bold text-zinc-900">
                        {t.category || (t.type === "income" ? "Pemasukan" : "Pengeluaran")}
                      </p>
                      <p className="truncate text-xs text-zinc-400">
                        {(t.note ? t.note + " · " : "") + formatDateID(t.date)}
                      </p>
                      {t.amountExpr && (
                        <p className="truncate text-xs text-zinc-500" data-testid={`tx-expr-${t.id}`}>
                          dari {t.amountExpr}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 pl-3">
                      <span
                        className={`font-mono text-sm font-bold ${t.type === "income" ? "text-emerald-600" : "text-rose-600"}`}
                      >
                        {t.type === "income" ? "+" : "-"}
                        {formatCompact(t.amount)}
                      </span>
                      <button
                        data-testid={`tx-edit-${t.id}`}
                        onClick={() => navigate(`/app/catat?edit=${t.id}`)}
                        className="text-zinc-300 hover:text-zinc-700"
                        aria-label="Edit transaksi"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`tx-delete-${t.id}`} className="text-zinc-300 hover:text-rose-500">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Hapus transaksi?</AlertDialogTitle>
                            <AlertDialogDescription>Tindakan ini tidak bisa dibatalkan.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Batal</AlertDialogCancel>
                            <AlertDialogAction
                              data-testid={`tx-delete-confirm-${t.id}`}
                              onClick={() => deleteTransaction(t.id)}
                              className="bg-rose-600 hover:bg-rose-700"
                            >
                              Hapus
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 flex items-start gap-2 rounded-2xl bg-emerald-50 p-3">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
              <p className="text-xs leading-relaxed text-emerald-700">
                Angka keuangan yang Anda masukkan tetap pribadi dan tidak dapat dilihat oleh admin CeKKeU.
              </p>
            </div>
          </div>
        </div>
      ) : (
        /* Desktop: kartu analisis jadi grid 2 kolom (urutan DOM/mobile tak berubah) */
        <div className="space-y-4 lg:grid lg:grid-cols-2 lg:items-start lg:gap-5 lg:space-y-0" data-testid="analisis-content">
          <div className="flex justify-end lg:col-span-2">
            <button
              data-testid="export-pdf-btn"
              onClick={() => { exportInsightPdf(finance, { months: 6 }); toast.success("PDF dibuat di perangkat Anda"); }}
              className="inline-flex h-9 items-center gap-1.5 rounded-full border border-zinc-300 px-4 text-xs font-semibold text-zinc-700"
            >
              <FileDown className="h-3.5 w-3.5" /> Ekspor PDF
            </button>
          </div>
          <DailyHealthPanel health={health} onSetup={() => navigate("/app/keuangan")} />
          <MonthlySummary finance={finance} onSetup={() => navigate("/app/keuangan")} />
          <WeeklyRecapCard recap={recap} />
          <MonthlyTrends transactions={finance?.transactions || []} />
          <BudgetTargets finance={finance} onSetup={() => navigate("/app/keuangan")} />
          <button
            data-testid="open-simulator"
            onClick={() => navigate("/app/simulasi")}
            className="flex w-full items-center gap-3 rounded-3xl border border-zinc-200 bg-white p-5 text-left shield-shadow"
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-zinc-900 text-white">
              <FlaskConical className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-heading text-sm font-bold text-zinc-900">Simulasi "Bagaimana Jika"</p>
              <p className="mt-0.5 text-xs text-zinc-500">Uji dampak penghasilan turun, biaya naik, atau pengeluaran tak terduga.</p>
            </div>
            <ArrowRight className="h-5 w-5 shrink-0 text-zinc-300" />
          </button>
        </div>
      )}
    </div>
  );
}
